/**
 * AuthGuard Component (Shared)
 * 
 * Unified protected route wrapper for admin-console and agent-ui.
 * Verifies:
 * 1. User is authenticated (via Kratos session)
 * 2. Session has required AAL (Authenticator Assurance Level)
 * 
 * Features:
 * - Loop detection: prevents infinite auth redirects (3 redirects in 10s)
 * - MFA enforcement: redirects to setup/challenge based on credential state
 * - Error recovery UI: retry or clear session on persistent failures
 * - HOC variant: withAuthGuard() for class/function components
 * 
 * Redirects to appropriate auth flow if requirements not met.
 */

import React, { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getIdentityBrowserUrl } from '../../lib/identity-client';
import { isFederatedSession } from '../../types/auth';
import { Loader2, AlertTriangle } from 'lucide-react';

// =============================================================================
// Loop Detection
// =============================================================================

const REDIRECT_KEY = 'auth_redirect_count';
const REDIRECT_TIMESTAMP_KEY = 'auth_redirect_timestamp';
const MAX_REDIRECTS = 3;
const REDIRECT_WINDOW_MS = 10000; // 10 seconds

function checkRedirectLoop(): boolean {
  const now = Date.now();
  const timestamp = parseInt(sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY) || '0', 10);
  const count = parseInt(sessionStorage.getItem(REDIRECT_KEY) || '0', 10);
  
  // Reset if outside window
  if (now - timestamp > REDIRECT_WINDOW_MS) {
    sessionStorage.setItem(REDIRECT_KEY, '1');
    sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, now.toString());
    return false;
  }
  
  // Increment counter
  const newCount = count + 1;
  sessionStorage.setItem(REDIRECT_KEY, newCount.toString());
  
  // Check if loop detected
  return newCount > MAX_REDIRECTS;
}

function clearRedirectCounter(): void {
  sessionStorage.removeItem(REDIRECT_KEY);
  sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
}

// AR challenge returns cross-origin with ?mfa_skipped=1 when the identity
// has no MFA method (federated SSO accounts). Consume it once so this app
// (admin-console / agent-ui) stops bouncing aal1 sessions to the AR
// challenge forever (ISS-285). Stored per-origin in sessionStorage.
const MFA_SKIPPED_PARAM = 'mfa_skipped';

export function consumeMfaSkippedMarker(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get(MFA_SKIPPED_PARAM) === '1') {
    sessionStorage.setItem('mfa_not_configured', '1');
    params.delete(MFA_SKIPPED_PARAM);
    const cleaned = params.toString();
    const newUrl = `${window.location.pathname}${cleaned ? `?${cleaned}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }
}

// =============================================================================
// Types
// =============================================================================

export interface AuthGuardProps {
  children: ReactNode;
  /** Require AAL2 (MFA configured and verified) */
  requireMfa?: boolean;
  /** Fallback component while loading */
  fallback?: ReactNode;
}

// =============================================================================
// Error Fallback (Loop Detected)
// =============================================================================

function AuthErrorFallback({ onRetry, onClearSession }: { onRetry: () => void; onClearSession: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full mx-4 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Authentication Error
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Unable to establish a session. This might be caused by:
          </p>
          <ul className="text-sm text-gray-500 dark:text-gray-400 text-left list-disc list-inside">
            <li>Cookies are blocked or disabled</li>
            <li>Session has expired</li>
            <li>Authentication service is unavailable</li>
            <li>Rate limiting (too many attempts)</li>
          </ul>
          <div className="flex gap-3 mt-4">
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={onClearSession}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Clear Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Loading Fallback
// =============================================================================

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        <p className="text-gray-600 dark:text-gray-400">Verifying session...</p>
      </div>
    </div>
  );
}

// =============================================================================
// AuthGuard Component
// =============================================================================

export function AuthGuard({ 
  children, 
  requireMfa = false,
  fallback,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, session, login, logout, refresh } = useAuth();
  const [isCheckingMfa, setIsCheckingMfa] = useState(true);
  const [hasLoopError, setHasLoopError] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Consume the cross-origin marker left by the AR challenge when the
    // identity has no MFA method (ISS-285). Must run before the MFA check.
    consumeMfaSkippedMarker();

    // Not authenticated - redirect to login (with loop detection)
    if (!isAuthenticated) {
      if (checkRedirectLoop()) {
        console.error('Auth redirect loop detected - stopping redirects');
        setHasLoopError(true);
        setIsCheckingMfa(false);
        return;
      }
      
      login(window.location.href);
      return;
    }

    // Successfully authenticated - clear redirect counter
    clearRedirectCounter();

    // Check MFA requirement
    if (requireMfa && session) {
      const aal = session.authenticator_assurance_level;

      // Federated sessions get MFA from the IdP — Kratos AAL2 is unreachable
      // for them, so never bounce to the Kratos challenge (ISS-285).
      if (
        aal === 'aal1' &&
        sessionStorage.getItem('mfa_not_configured') !== '1' &&
        !isFederatedSession(session)
      ) {
        // identity.credentials.totp is NOT available in Kratos public API,
        // so we cannot reliably know whether TOTP is configured. Redirect
        // to the AR challenge, which short-circuits (mfa_skipped marker)
        // when the identity has no MFA method. return_to brings the user
        // back to this app after the challenge (ISS-285).
        if (checkRedirectLoop()) {
          console.error('Auth redirect loop detected (MFA) - stopping redirects');
          setHasLoopError(true);
          setIsCheckingMfa(false);
          return;
        }
        const returnTo = encodeURIComponent(window.location.href);
        window.location.href = `${getIdentityBrowserUrl()}/auth/mfa/challenge?return_to=${returnTo}`;
        return;
      }
    }

    setIsCheckingMfa(false);
  }, [isLoading, isAuthenticated, session, requireMfa, login]);

  // Handle retry from error screen
  const handleRetry = () => {
    clearRedirectCounter();
    setHasLoopError(false);
    setIsCheckingMfa(true);
    refresh();
  };

  const handleClearSession = () => {
    clearRedirectCounter();
    sessionStorage.clear();
    logout();
  };

  // Show error if loop detected
  if (hasLoopError) {
    return <AuthErrorFallback onRetry={handleRetry} onClearSession={handleClearSession} />;
  }

  // Still loading or checking
  if (isLoading || isCheckingMfa) {
    return fallback || <LoadingFallback />;
  }

  // Not authenticated (redirect in progress)
  if (!isAuthenticated) {
    return fallback || <LoadingFallback />;
  }

  // MFA check passed or not required
  return <>{children}</>;
}

// =============================================================================
// Higher Order Component Version
// =============================================================================

export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<AuthGuardProps, 'children'>
) {
  return function ProtectedComponent(props: P) {
    return (
      <AuthGuard {...options}>
        <Component {...props} />
      </AuthGuard>
    );
  };
}

export default AuthGuard;
